import { useState } from 'react';
import type { ConversationTrace } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { SlackPreview } from '@/components/golden-answer';
import { getModelShortName, formatDuration } from './utils';
import { ArrowDown } from 'lucide-react';

interface PhaseDetailsProps {
  phaseKey: string;
  trace: ConversationTrace;
}

// Section header component for Input/Output
function SectionHeader({ title, type }: { title: string; type: 'input' | 'output' | 'meta' }) {
  const colors = {
    input: 'bg-blue-50 text-blue-700 border-blue-200',
    output: 'bg-green-50 text-green-700 border-green-200',
    meta: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <div className={`text-xs font-semibold px-2 py-1 rounded border ${colors[type]} mb-2`}>
      {title}
    </div>
  );
}

// Code block for showing data
function DataBlock({ data, label }: { data: string | null | undefined; label?: string }) {
  if (!data) return <span className="text-muted-foreground italic text-xs">No data</span>;
  return (
    <div className="space-y-1">
      {label && <span className="text-[10px] text-muted-foreground uppercase">{label}</span>}
      <div className="text-xs p-2 bg-muted/50 rounded border max-h-40 overflow-y-auto whitespace-pre-wrap font-mono">
        {data}
      </div>
    </div>
  );
}

export function PhaseDetails({ phaseKey, trace }: PhaseDetailsProps) {
  if (phaseKey === 'phase1' && trace.phase1) {
    const phase1 = trace.phase1;
    return (
      <div className="space-y-4 pt-3">
        {/* Input Section */}
        <div>
          <SectionHeader title="Input" type="input" />
          <div className="space-y-2 pl-2">
            <DataBlock data={phase1.data.originalQuery} label="Original Query" />
            {phase1.data.isThreadReply && phase1.data.threadContext && (
              <DataBlock 
                data={JSON.stringify(phase1.data.threadContext, null, 2)} 
                label="Thread Context" 
              />
            )}
          </div>
        </div>

        {/* Arrow indicator */}
        <div className="flex justify-center">
          <ArrowDown className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Output Section */}
        <div>
          <SectionHeader title="Output" type="output" />
          <div className="space-y-2 pl-2">
            <DataBlock data={phase1.data.enrichedQuery} label="Enriched Query" />
        {phase1.data.searchTerms && phase1.data.searchTerms.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase">Search Terms</span>
                <div className="flex flex-wrap gap-1">
                  {phase1.data.searchTerms.map((term, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px]">{term}</Badge>
                  ))}
                </div>
              </div>
        )}
        {phase1.data.detectedAbbreviations && phase1.data.detectedAbbreviations.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase">Expanded Abbreviations</span>
                <div className="flex flex-wrap gap-1">
                  {phase1.data.detectedAbbreviations.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">
                      {a.abbrev} → {a.full}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metadata Section */}
        <div>
          <SectionHeader title="Metadata" type="meta" />
          <div className="space-y-1 pl-2 text-xs">
            {phase1.model && <DetailRow label="Model" value={getModelShortName(phase1.model)} />}
        {phase1.inputTokens !== undefined && (
          <DetailRow label="Tokens" value={`${phase1.inputTokens} in / ${phase1.outputTokens || 0} out`} />
        )}
        {phase1.durationMs !== undefined && (
          <DetailRow label="Duration" value={formatDuration(phase1.durationMs)} />
        )}
          </div>
        </div>
      </div>
    );
  }

  if (phaseKey === 'phase2' && trace.phase2) {
    const phase2 = trace.phase2;
    // Get the enriched query from phase1 as input
    const inputQuery = trace.phase1?.data.enrichedQuery || trace.phase1?.data.originalQuery || '(no query)';
    
    return (
      <div className="space-y-4 pt-3">
        {/* Input Section */}
        <div>
          <SectionHeader title="Input" type="input" />
          <div className="space-y-2 pl-2">
            <DataBlock data={inputQuery} label="Search Query" />
          </div>
        </div>

        {/* Arrow indicator */}
        <div className="flex justify-center">
          <ArrowDown className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Output Section */}
        <div>
          <SectionHeader title="Output" type="output" />
          <div className="space-y-3 pl-2">
            <div className="flex items-center gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Results:</span>{' '}
                <span className="font-semibold">{phase2.data.totalResults || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Top Score:</span>{' '}
                <span className="font-semibold">{phase2.data.topScore?.toFixed(3) || '—'}</span>
              </div>
            </div>
            
            {phase2.data.allResults && phase2.data.allResults.length > 0 && (
              <div>
                <span className="text-[10px] text-muted-foreground uppercase">Retrieved Documents</span>
                <div className="mt-1 space-y-1 max-h-60 overflow-y-auto">
                  {phase2.data.allResults.slice(0, 5).map((result) => (
                    <div key={result.id} className="text-xs p-2 bg-background rounded border">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{result.source}</Badge>
                        <span className="font-medium truncate flex-1">{result.title}</span>
                        <span className="text-muted-foreground font-mono">{result.score?.toFixed(3) ?? '—'}</span>
                      </div>
                      {result.contentPreview && (
                        <p className="mt-1 text-muted-foreground line-clamp-2">{result.contentPreview}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metadata Section */}
        <div>
          <SectionHeader title="Metadata" type="meta" />
          <div className="space-y-1 pl-2 text-xs">
        {phase2.embeddingModel && (
          <DetailRow label="Embedding Model" value={getModelShortName(phase2.embeddingModel)} />
        )}
        {phase2.embeddingDurationMs !== undefined && (
          <DetailRow label="Embedding Time" value={formatDuration(phase2.embeddingDurationMs)} />
        )}
        {phase2.searchDurationMs !== undefined && (
          <DetailRow label="Search Time" value={formatDuration(phase2.searchDurationMs)} />
        )}
        {phase2.durationMs !== undefined && (
          <DetailRow label="Total Duration" value={formatDuration(phase2.durationMs)} />
        )}
          </div>
        </div>
      </div>
    );
  }

  if (phaseKey === 'phase3' && trace.phase3) {
    const phase3 = trace.phase3;
    const phase2Results = trace.phase2?.data.allResults?.slice(0, 3) || [];
    
    return (
      <div className="space-y-4 pt-3">
        {/* Input Section */}
        <div>
          <SectionHeader title="Input" type="input" />
          <div className="space-y-2 pl-2">
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground uppercase">Search Results to Assess</span>
              <div className="text-xs p-2 bg-muted/50 rounded border">
                {phase2Results.length > 0 ? (
                  <div className="space-y-1">
                    {phase2Results.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{r.source}</Badge>
                        <span className="truncate">{r.title}</span>
                        <span className="text-muted-foreground ml-auto">{r.score?.toFixed(3) ?? '—'}</span>
                      </div>
                    ))}
                    {(trace.phase2?.data.totalResults || 0) > 3 && (
                      <span className="text-muted-foreground">...and {(trace.phase2?.data.totalResults || 0) - 3} more</span>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground italic">No results to assess</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Arrow indicator */}
        <div className="flex justify-center">
          <ArrowDown className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Output Section */}
        <div>
          <SectionHeader title="Output" type="output" />
          <div className="space-y-3 pl-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded border bg-background">
                <span className="text-[10px] text-muted-foreground uppercase block mb-1">Has Results</span>
                <Badge variant={phase3.data.hasResults ? 'default' : 'secondary'} className="text-xs">
              {phase3.data.hasResults ? 'Yes' : 'No'}
            </Badge>
              </div>
              <div className="p-2 rounded border bg-background">
                <span className="text-[10px] text-muted-foreground uppercase block mb-1">Relevant Topic</span>
                <Badge variant={phase3.data.isRelevantTopic ? 'default' : 'secondary'} className="text-xs">
              {phase3.data.isRelevantTopic ? 'Yes' : 'No'}
            </Badge>
              </div>
            </div>
            <div className="p-2 rounded border bg-background">
              <span className="text-[10px] text-muted-foreground uppercase block mb-1">Response Type</span>
              <Badge 
                variant={phase3.data.responseType === 'answer' ? 'default' : 'secondary'}
                className="text-xs"
              >
                {phase3.data.responseType || '—'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Metadata Section */}
        <div>
          <SectionHeader title="Metadata" type="meta" />
          <div className="space-y-1 pl-2 text-xs">
            {phase3.model && <DetailRow label="Model" value={getModelShortName(phase3.model)} />}
        {phase3.inputTokens !== undefined && (
          <DetailRow label="Tokens" value={`${phase3.inputTokens} in / ${phase3.outputTokens || 0} out`} />
        )}
        {phase3.durationMs !== undefined && (
          <DetailRow label="Duration" value={formatDuration(phase3.durationMs)} />
        )}
          </div>
        </div>
      </div>
    );
  }

  if (phaseKey === 'phase4a' && trace.phase4) {
    return <ReasonerPhaseDetails trace={trace} />;
  }

  if (phaseKey === 'phase4b' && trace.phase4) {
    return <FormatterPhaseDetails trace={trace} />;
  }

  return (
    <div className="py-4 text-center text-sm text-muted-foreground">
      No data available for this phase
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-muted-foreground w-28 shrink-0">{label}:</span>
      <span className="text-foreground break-words">{value}</span>
    </div>
  );
}

// Reasoner phase with clear Input/Output sections
function ReasonerPhaseDetails({ trace }: { trace: ConversationTrace }) {
  const [showThinking, setShowThinking] = useState(false);
  const phase4 = trace.phase4!;
  
  // Build context input from search results
  const searchResults = trace.phase2?.data.allResults?.slice(0, 5) || [];

  return (
    <div className="space-y-4 pt-3">
      {/* Input Section */}
      <div>
        <SectionHeader title="Input" type="input" />
        <div className="space-y-2 pl-2">
          <DataBlock data={trace.phase1?.data.originalQuery} label="Original Query" />
          <div className="space-y-1">
            <span className="text-[10px] text-muted-foreground uppercase">Context (Search Results)</span>
            <div className="text-xs p-2 bg-muted/50 rounded border max-h-32 overflow-y-auto">
              {searchResults.length > 0 ? (
                <div className="space-y-1">
                  {searchResults.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] shrink-0">{r.source}</Badge>
                      <span className="truncate text-xs">{r.title}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground italic">No context provided</span>
      )}
            </div>
          </div>
        </div>
      </div>

      {/* Arrow indicator */}
      <div className="flex justify-center">
        <ArrowDown className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Output Section */}
      <div>
        <SectionHeader title="Output" type="output" />
        <div className="space-y-3 pl-2">
          {/* Thinking toggle (if available) */}
          {phase4.data.reasonerThinking && (
            <div>
          <button
                onClick={() => setShowThinking(!showThinking)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1"
          >
                <span className="text-[10px] uppercase">Chain of Thought</span>
                <span className="text-[10px]">{showThinking ? '▼' : '▶'}</span>
          </button>
              {showThinking && (
                <div className="text-xs p-2 bg-purple-50 border border-purple-200 rounded max-h-40 overflow-y-auto whitespace-pre-wrap font-mono text-purple-800">
                  {phase4.data.reasonerThinking}
        </div>
              )}
            </div>
          )}
          
          {/* Main output */}
          <div className="space-y-1">
            <span className="text-[10px] text-muted-foreground uppercase">Reasoned Answer</span>
            {phase4.data.reasonerAnswer ? (
              <div className="text-xs p-2 bg-green-50 border border-green-200 rounded max-h-60 overflow-y-auto whitespace-pre-wrap">
              {phase4.data.reasonerAnswer}
            </div>
          ) : (
              <div className="text-xs p-2 text-muted-foreground italic">No output available</div>
            )}
          </div>
        </div>
      </div>

      {/* Metadata Section */}
      <div>
        <SectionHeader title="Metadata" type="meta" />
        <div className="space-y-1 pl-2 text-xs">
          {phase4.reasonerModel && <DetailRow label="Model" value={getModelShortName(phase4.reasonerModel)} />}
          {phase4.reasonerInputTokens !== undefined && (
            <DetailRow label="Tokens" value={`${phase4.reasonerInputTokens} in / ${phase4.reasonerOutputTokens || 0} out`} />
          )}
        </div>
      </div>
    </div>
  );
}

// Formatter phase with clear Input/Output sections
function FormatterPhaseDetails({ trace }: { trace: ConversationTrace }) {
  const [outputFormat, setOutputFormat] = useState<'raw' | 'preview'>('preview');
  const phase4 = trace.phase4!;

  return (
    <div className="space-y-4 pt-3">
      {/* Input Section */}
      <div>
        <SectionHeader title="Input" type="input" />
        <div className="space-y-2 pl-2">
          <div className="space-y-1">
            <span className="text-[10px] text-muted-foreground uppercase">Reasoner Answer (to format)</span>
            {phase4.data.reasonerAnswer ? (
              <div className="text-xs p-2 bg-blue-50 border border-blue-200 rounded max-h-40 overflow-y-auto whitespace-pre-wrap">
                {phase4.data.reasonerAnswer}
              </div>
            ) : (
              <div className="text-xs p-2 text-muted-foreground italic">No input available</div>
            )}
          </div>
        </div>
      </div>

      {/* Arrow indicator */}
      <div className="flex justify-center">
        <ArrowDown className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Output Section */}
      <div>
        <SectionHeader title="Output" type="output" />
        <div className="space-y-3 pl-2">
          {/* Fallback indicator */}
          {phase4.data.fallbackUsed && (
            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              ⚠️ Fallback formatter was used (primary formatting failed)
            </div>
          )}
          
          {/* Output format toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground uppercase">Slack mrkdwn</span>
            <div className="flex gap-1 border rounded p-0.5 ml-auto">
              <button
                onClick={() => setOutputFormat('raw')}
                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                  outputFormat === 'raw'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Raw
              </button>
              <button
                onClick={() => setOutputFormat('preview')}
                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                  outputFormat === 'preview'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Preview
              </button>
            </div>
        </div>

          {/* Formatted output */}
          {phase4.data.formattedResponse ? (
            outputFormat === 'raw' ? (
              <div className="text-xs p-2 bg-green-50 border border-green-200 rounded max-h-60 overflow-y-auto whitespace-pre-wrap font-mono">
                {phase4.data.formattedResponse}
              </div>
            ) : (
              <div className="rounded border overflow-hidden bg-white">
                <SlackPreview
                  blocks={[]}
                  formattedText={phase4.data.formattedResponse}
                />
              </div>
            )
          ) : (
            <div className="text-xs p-2 text-muted-foreground italic">No output available</div>
          )}
        </div>
      </div>

      {/* Metadata Section */}
      <div>
        <SectionHeader title="Metadata" type="meta" />
        <div className="space-y-1 pl-2 text-xs">
          {phase4.formatterModel && <DetailRow label="Model" value={getModelShortName(phase4.formatterModel)} />}
          {phase4.formatterInputTokens !== undefined && (
            <DetailRow label="Tokens" value={`${phase4.formatterInputTokens} in / ${phase4.formatterOutputTokens || 0} out`} />
          )}
        </div>
      </div>
    </div>
  );
}
